import React, { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
  InputAdornment, Switch, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import {
  Badge, Card, FieldLabel, GhostButton, LinkButton, Notice, OnboardingPage, OutlineButton, PageHeader, PrimaryButton, StatCard,
} from '../components/PartnerSignups/onboardingUi'
import SignInsDialog from '../components/Security/SignInsDialog'
import IsoDialog from '../components/Agents/IsoDialog'
import IsoDetailsDialog from '../components/Agents/IsoDetailsDialog'
import AssignRestaurantDialog from '../components/Agents/AssignRestaurantDialog'
import CreditedRestaurantsDialog from '../components/Agents/CreditedRestaurantsDialog'
import AssignRestaurantsDialog from '../components/Agents/AssignRestaurantsDialog'
import { startViewAs } from '../utils/viewAs'
import { BANK_FIELDS, ISO_RATE_FIELDS, MONTH_FIELDS, PAYOUT_FIELDS, PayoutsTable, rateText } from '../components/Agents/isoParts'
import { timeAgo } from '../utils/authToken'

/**
 * Super admin: ISOs & Agents. RushMyFood's own sales agents
 * (resolvers/agent.js): create an agent login, set what each earns — a flat
 * amount per approved restaurant, and a % of every online order's subtotal
 * from those restaurants (backend utils/agentEarnings.js, settled by month) —
 * and mark commissions paid. ISOs (resolvers/iso.js): companies with their
 * own agents, earning a rate per platform; their payouts are settled by month
 * too (backend utils/isoEarnings.js). Styled like the merchant sign-up form
 * (onboardingUi).
 */

const AGENTS = gql`
  query Agents {
    agents {
      _id name email phone code link commission orderRate extraShare isActive isEmailVerified lastLoginAt createdAt
      stats { invites started signed approved rejected pendingAmount paidAmount restaurants signedUp assigned }
      thisMonth { label orders subtotal amount }
    }
  }
`
const COMMISSIONS = gql`
  query AgentCommissions($status: String) {
    agentCommissions(status: $status) {
      _id agentId agentName kind restaurantName period periodLabel ordersCount subtotal amount status note createdAt paidAt
      detail { restaurantId restaurantName rate orders subtotal extra amount }
    }
  }
`
const ISOS = gql`
  query Isos {
    isos {
      _id vendorId corporateName email phone address1 address2 city state zip taxIdType taxId payoutTo extraShare code link
      isActive isEmailVerified lastLoginAt createdAt
      bank { ${BANK_FIELDS} }
      rates { ${ISO_RATE_FIELDS} }
      stats { agents activeAgents started approved accounts accountsSignedUp accountsAssigned pendingAmount paidAmount owedByIso }
      thisMonth { ${MONTH_FIELDS} }
    }
  }
`
const ISO_PAYOUTS = gql`
  query IsoPayouts($status: String) {
    isoPayouts(status: $status) { ${PAYOUT_FIELDS} }
  }
`
// A super admin opening an agent's or ISO's portal as them (utils/viewAs.js).
const VIEW_AS = gql`mutation ViewAsAgent($vendorId: ID!) { viewAsAgent(vendorId: $vendorId) { token userId userType email name expiresAt } }`
const SET_ISO_PAYOUT = gql`mutation SetIsoPayoutStatus($id: ID!, $status: String!) { setIsoPayoutStatus(id: $id, status: $status) { _id status paidAt } }`
const CREATE = gql`mutation CreateAgent($input: AgentInput!) { createAgent(input: $input) { _id code } }`
const UPDATE = gql`mutation UpdateAgent($id: ID!, $input: AgentUpdateInput!) { updateAgent(id: $id, input: $input) { _id commission orderRate extraShare isActive } }`
const SET_STATUS = gql`mutation SetAgentCommissionStatus($id: ID!, $status: String!) { setAgentCommissionStatus(id: $id, status: $status) { _id status paidAt } }`

const money = v => `$${(Number(v) || 0).toFixed(2)}`
const pct = v => `${Number(Number(v || 0).toFixed(2))}%`
const day = v => (v ? new Date(v).toLocaleDateString() : '—')
// Badge tone for each commission status.
const COMMISSION_KIND = {
  PENDING: 'amber',
  PAID: 'green',
  VOID: 'neutral',
}

// The MUI tables: muted 13px semibold header on the footer tint, roomier
// rows, outer columns padded to the card's edge, no rule under the last row.
const headCell = { fontWeight: 600, fontSize: 13, color: T.muted, whiteSpace: 'nowrap', bgcolor: T.footerBg }
const tableSx = {
  '& .MuiTableCell-root': { px: 2 },
  '& .MuiTableCell-body': { py: 1.5 },
  '& .MuiTableCell-root:first-of-type': { pl: { xs: 2, sm: 3 } },
  '& .MuiTableCell-root:last-of-type': { pr: { xs: 2, sm: 3 } },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 },
}
const tabSx = { textTransform: 'none', fontWeight: 600, fontSize: 15, color: T.muted, minHeight: 52, '&.Mui-selected': { color: T.text } }
// Void / Undo: a quieter text action than the link color.
const mutedLink = { color: T.muted, '&:hover': { color: T.text, background: T.fill } }

function AgentDialog({ agent, onClose, onSaved }) {
  const editing = !!agent
  const [f, setF] = useState({
    name: agent ? agent.name : '',
    email: agent ? agent.email : '',
    phone: agent ? agent.phone || '' : '',
    commission: agent ? String(agent.commission ?? '') : '',
    // New agents start at 5% of the order subtotal.
    orderRate: agent ? String(agent.orderRate ?? '') : '5',
    // Their share of a restaurant's fees above the minimums; 50% by default.
    extraShare: agent ? String(agent.extraShare ?? '') : '50',
    isActive: agent ? agent.isActive : true,
  })
  const [error, setError] = useState(null)
  const [create, { loading: creating }] = useMutation(CREATE)
  const [update, { loading: updating }] = useMutation(UPDATE)
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }))

  const save = async () => {
    setError(null)
    const commission = f.commission === '' ? null : Number(f.commission)
    const orderRate = f.orderRate === '' ? null : Number(f.orderRate)
    const extraShare = f.extraShare === '' ? null : Number(f.extraShare)
    try {
      if (editing) {
        await update({ variables: { id: agent._id, input: { name: f.name, phone: f.phone, commission, orderRate, extraShare, isActive: f.isActive } } })
      } else {
        await create({ variables: { input: { name: f.name, email: f.email, phone: f.phone || null, commission, orderRate, extraShare } } })
      }
      onSaved()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${agent.name}` : 'New agent'}</DialogTitle>
      <DialogContent dividers>
        {error && <Notice severity="error">{error}</Notice>}
        {!editing && (
          <Typography variant="body2" sx={{ color: T.text2, mb: 2.5 }}>
            The agent gets an email with a temporary password and a link to verify their address. They sign in on this site and only see their agent portal.
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldLabel htmlFor="agent-name" label="Name" />
            <TextField id="agent-name" fullWidth size="small" required value={f.name} onChange={set('name')} />
          </Grid>
          <Grid item xs={12} sm={7}>
            <FieldLabel htmlFor="agent-email" label="Email" />
            <TextField id="agent-email" fullWidth size="small" type="email" required disabled={editing} value={f.email} onChange={set('email')} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FieldLabel htmlFor="agent-phone" label="Phone" optional />
            <TextField id="agent-phone" fullWidth size="small" value={f.phone} onChange={set('phone')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="agent-commission" label="Per approved restaurant" optional />
            <TextField
              id="agent-commission" fullWidth size="small" type="number"
              value={f.commission} onChange={set('commission')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="A one-time amount when a restaurant they brought in is approved."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="agent-order-rate" label="On every order" />
            <TextField
              id="agent-order-rate" fullWidth size="small" type="number"
              value={f.orderRate} onChange={set('orderRate')}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              InputProps={{ endAdornment: <InputAdornment position="end">% of subtotal</InputAdornment> }}
              helperText="Of every online order from their restaurants, for as long as each stays with us."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="agent-extra-share" label="Above the minimums" />
            <TextField
              id="agent-extra-share" fullWidth size="small" type="number"
              value={f.extraShare} onChange={set('extraShare')}
              inputProps={{ min: 0, max: 100, step: 5 }}
              InputProps={{ endAdornment: <InputAdornment position="end">% of the extra</InputAdornment> }}
              helperText="Their share of a restaurant's fees above the minimums (e.g. delivery at 30% vs the 20% minimum)."
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ color: T.hint, fontSize: 13 }}>
              All three apply to restaurants approved from now on; each restaurant keeps the rates it was approved with.
            </Typography>
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: 14 } }}
                control={<Switch checked={f.isActive} onChange={e => setF(x => ({ ...x, isActive: e.target.checked }))} />}
                label={f.isActive ? 'Active' : 'Deactivated'}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={save} disabled={creating || updating || !f.name || (!editing && !f.email)}>
          {creating || updating ? 'Saving…' : editing ? 'Save' : 'Create agent'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}

/** An ISO's rates on one line each, platforms it earns nothing on left out. */
function RatesCell({ rates }) {
  const set = (rates || []).filter(r => rateText(r) !== '—')
  if (!set.length) return <Typography sx={{ color: T.amberText, fontSize: 13 }}>No rates set</Typography>
  return set.map(r => (
    <Typography key={r.platform} sx={{ fontSize: 13, color: T.text2, whiteSpace: 'nowrap' }}>{r.label}: {rateText(r)}</Typography>
  ))
}

/** The name, as a link that opens their portal as them (utils/viewAs.js). */
function PortalName({ name, onClick, disabled }) {
  return (
    <Box
      component="button" type="button" onClick={onClick} disabled={disabled} title={disabled ? undefined : 'Open their portal as them'}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, p: 0, border: 0, background: 'none', font: 'inherit', textAlign: 'left',
        fontWeight: 600, fontSize: 14, color: T.text, cursor: disabled ? 'default' : 'pointer',
        '&:hover:not(:disabled)': { color: T.link, textDecoration: 'underline' }, '&:disabled': { color: T.text },
      }}
    >
      {name}
      {!disabled && <Icon.External size={13} />}
    </Box>
  )
}

/** The small button beside a name that picks restaurants for them. */
function AssignIcon({ name, onClick }) {
  return (
    <Tooltip title={`Assign restaurants to ${name}`}>
      <IconButton size="small" onClick={onClick} aria-label={`Assign restaurants to ${name}`} sx={{ color: T.link, p: 0.5, '&:hover': { background: T.redTint } }}>
        <Icon.Store size={15} />
      </IconButton>
    </Tooltip>
  )
}

/** How many restaurants they earn on now, signed up through them and assigned by hand. */
function RestaurantCount({ total, signedUp, assigned, onClick }) {
  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <LinkButton size="small" onClick={onClick} sx={{ ml: -1, minHeight: 30, fontWeight: 700, fontSize: 15 }}>{total || 0}</LinkButton>
      <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{signedUp || 0} signed up · {assigned || 0} assigned</Typography>
    </Box>
  )
}

const FILTERS = [['PENDING', 'Owed'], ['PAID', 'Paid'], ['VOID', 'Void'], ['', 'All']]
const chipSx = { height: 34, px: 0.5, fontSize: 14, '&.MuiChip-outlined': { borderColor: T.input, color: T.text2, background: T.card }, '&.MuiChip-outlined:hover': { background: T.fill } }

export default function Agents() {
  const [tab, setTab] = useState(0)
  const [dialog, setDialog] = useState(null)
  const [isoDialog, setIsoDialog] = useState(null)
  const [isoDetails, setIsoDetails] = useState(null)
  // Credit an onboarded restaurant by hand (backend utils/restaurantCredit.js):
  // {} from the header, { vendorId } from someone's restaurants.
  const [assigning, setAssigning] = useState(null)
  // Whose restaurants are open: { name, vendorId } or { name, isoId, vendorId }.
  const [creditedFor, setCreditedFor] = useState(null)
  // Picking several restaurants for one of them: { vendorId, name }.
  const [pickingFor, setPickingFor] = useState(null)
  const [notice, setNotice] = useState(null)
  const [search, setSearch] = useState('')
  const [commissionFilter, setCommissionFilter] = useState('PENDING')
  const [isoPayoutFilter, setIsoPayoutFilter] = useState('PENDING')
  // Sign-ins and sign out of all devices (backend migration 137).
  const [signInsTarget, setSignInsTarget] = useState(null)
  const [actionError, setActionError] = useState(null)
  const { data, loading, error, refetch } = useQuery(AGENTS, { fetchPolicy: 'network-only' })
  const { data: cData, loading: cLoading, refetch: refetchCommissions } = useQuery(COMMISSIONS, {
    variables: { status: commissionFilter || null },
    fetchPolicy: 'network-only',
  })
  const { data: iData, loading: iLoading, error: iError, refetch: refetchIsos } = useQuery(ISOS, { fetchPolicy: 'network-only' })
  const { data: pData, loading: pLoading, refetch: refetchIsoPayouts } = useQuery(ISO_PAYOUTS, {
    variables: { status: isoPayoutFilter || null },
    fetchPolicy: 'network-only',
  })
  const [setStatus, { loading: settingStatus }] = useMutation(SET_STATUS)
  const [setIsoPayout, { loading: settingIsoPayout }] = useMutation(SET_ISO_PAYOUT)
  const [viewAs, { loading: openingPortal }] = useMutation(VIEW_AS)
  const allAgents = (data && data.agents) || []
  const allIsos = (iData && iData.isos) || []
  // The search narrows whichever tab is open: names, emails and link codes.
  const q = search.trim().toLowerCase()
  const hit = (...fields) => !q || fields.some(f => f && String(f).toLowerCase().includes(q))
  const agents = allAgents.filter(a => hit(a.name, a.email, a.code))
  const isos = allIsos.filter(i => hit(i.corporateName, i.email, i.code))
  const commissions = ((cData && cData.agentCommissions) || []).filter(c => hit(c.agentName, c.restaurantName))
  const isoPayouts = ((pData && pData.isoPayouts) || []).filter(p => hit(p.isoName, p.agentName, p.restaurantName))

  const openPortal = async (vendorId) => {
    setActionError(null)
    try {
      const res = await viewAs({ variables: { vendorId } })
      startViewAs(res.data.viewAsAgent)
    } catch (e) {
      setActionError(e.message)
    }
  }
  const refetchAll = () => Promise.all([refetch(), refetchIsos(), refetchCommissions()])
  const pendingTotal = allAgents.reduce((s, a) => s + ((a.stats && a.stats.pendingAmount) || 0), 0)
  const monthTotal = allAgents.reduce((s, a) => s + ((a.thisMonth && a.thisMonth.amount) || 0), 0)
  const isoOwed = allIsos.reduce((s, i) => s + ((i.stats && i.stats.pendingAmount) || 0), 0)

  const changeStatus = async (id, status) => {
    setActionError(null)
    try {
      await setStatus({ variables: { id, status } })
      await Promise.all([refetchCommissions(), refetch()])
    } catch (e) {
      setActionError(e.message)
    }
  }

  const changeIsoPayout = async (id, status) => {
    setActionError(null)
    try {
      await setIsoPayout({ variables: { id, status } })
      await Promise.all([refetchIsoPayouts(), refetchIsos()])
    } catch (e) {
      setActionError(e.message)
    }
  }

  return (
    <OnboardingPage>
      <PageHeader
        title="ISOs & Agents"
        subtitle="Sales agents and ISOs, the restaurants they bring in, and what they earn"
        actions={(
          <>
            <OutlineButton onClick={() => setAssigning({})}>
              <Icon.Store size={18} />
              Assign a restaurant
            </OutlineButton>
            <PrimaryButton onClick={() => setDialog({})}>
              <Icon.Plus size={18} />
              New agent
            </PrimaryButton>
            <PrimaryButton onClick={() => setIsoDialog({})}>
              <Icon.Plus size={18} />
              New ISO
            </PrimaryButton>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2, mb: 3 }}>
        {[
          { label: 'Active agents', value: allAgents.filter(a => a.isActive).length, tone: 'purple' },
          { label: 'Sign-ups via agents', value: allAgents.reduce((s, a) => s + ((a.stats && a.stats.started) || 0), 0), tone: 'info' },
          { label: 'Approved', value: allAgents.reduce((s, a) => s + ((a.stats && a.stats.approved) || 0), 0), tone: 'green' },
          { label: 'Commission owed', value: money(pendingTotal), tone: 'red' },
          { label: 'Owed to ISOs & their agents', value: money(isoOwed), tone: 'red' },
          { label: 'Order commission this month', value: money(monthTotal), tone: 'text' },
        ].map(s => (
          <StatCard key={s.label} value={s.value} label={s.label} tone={s.tone} />
        ))}
      </Box>

      {actionError && <Notice severity="error" onClose={() => setActionError(null)}>{actionError}</Notice>}
      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${T.divider}`, px: { xs: 1, sm: 2 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab label={`Agents (${agents.length})`} sx={tabSx} />
          <Tab label={`ISOs (${isos.length})`} sx={tabSx} />
          <Tab label="Commissions" sx={tabSx} />
          <Tab label="ISO payouts" sx={tabSx} />
        </Tabs>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <TextField
            size="small" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={['Search agents by name, email or link code', 'Search ISOs by name, email or link code', 'Search by agent or restaurant', 'Search by ISO, agent or restaurant'][tab]}
            inputProps={{ 'aria-label': 'Search' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Icon.Search size={16} /></InputAdornment> }}
            sx={{ width: { xs: '100%', sm: 380 } }}
          />
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          {tab === 0 ? (
            loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
              : error ? <Notice severity="error" sx={{ m: 2 }}>{error.message}</Notice>
                : agents.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>{q ? `No agent matches “${search.trim()}”.` : 'No agents yet. Create one to give them a sign-up link.'}</Typography>
                  : (
                    <Table size="small" sx={{ minWidth: 1160, ...tableSx }}>
                      <TableHead>
                        <TableRow>
                          {['Agent', 'Link code', 'Restaurants', 'Per approval', 'On orders', 'Invited', 'Started', 'Signed', 'Approved', 'This month', 'Owed', 'Paid', ''].map(h => (
                            <TableCell key={h} sx={headCell}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agents.map(a => (
                          <TableRow key={a._id} sx={{ opacity: a.isActive ? 1 : 0.55 }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PortalName name={a.name} disabled={openingPortal || !a.isActive} onClick={() => openPortal(a._id)} />
                                {a.isActive && <AssignIcon name={a.name} onClick={() => setPickingFor({ vendorId: a._id, name: a.name })} />}
                              </Box>
                              <Typography variant="caption" sx={{ color: T.muted, fontSize: 13 }}>{a.email}</Typography>
                              {!a.isEmailVerified && <Badge kind="amber" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>Email not verified</Badge>}
                              {!a.isActive && <Badge kind="red" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>Deactivated</Badge>}
                              {/* Recorded from backend migration 137 on. */}
                              <Typography sx={{ color: a.lastLoginAt ? T.text2 : T.hint, fontSize: 12.5, mt: 0.25 }} title={a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleString() : undefined}>
                                {a.lastLoginAt ? `Last login ${timeAgo(a.lastLoginAt)}` : 'No sign-in recorded yet'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13.5, color: T.text2, whiteSpace: 'nowrap' }}>{a.code}</TableCell>
                            <TableCell>
                              <RestaurantCount
                                total={a.stats.restaurants} signedUp={a.stats.signedUp} assigned={a.stats.assigned}
                                onClick={() => setCreditedFor({ name: a.name, vendorId: a._id })}
                              />
                            </TableCell>
                            <TableCell>{money(a.commission)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{pct(a.orderRate)}</TableCell>
                            <TableCell>{a.stats.invites}</TableCell>
                            <TableCell>{a.stats.started}</TableCell>
                            <TableCell>{a.stats.signed}</TableCell>
                            <TableCell>{a.stats.approved}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {money(a.thisMonth && a.thisMonth.amount)}
                              <Typography sx={{ color: T.hint, fontSize: 12.5 }}>
                                {a.thisMonth ? `${a.thisMonth.orders} order${a.thisMonth.orders === 1 ? '' : 's'}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: T.amberText, fontWeight: 600 }}>{money(a.stats.pendingAmount)}</TableCell>
                            <TableCell>{money(a.stats.paidAmount)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <LinkButton size="small" onClick={() => setDialog({ agent: a })}>Edit</LinkButton>
                                {a.isActive && <LinkButton size="small" disabled={openingPortal} onClick={() => openPortal(a._id)} sx={{ whiteSpace: 'nowrap' }}>Open portal</LinkButton>}
                                <LinkButton
                                  size="small" sx={{ whiteSpace: 'nowrap' }}
                                  onClick={() => setIsoDialog({ convertFrom: { _id: a._id, name: a.name, email: a.email, phone: a.phone, restaurants: a.stats.restaurants } })}
                                >
                                  Convert to ISO
                                </LinkButton>
                                <LinkButton size="small" onClick={() => setSignInsTarget(a)} sx={{ whiteSpace: 'nowrap' }}>Sign-ins</LinkButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )
          ) : tab === 1 ? (
            iLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
              : iError ? <Notice severity="error" sx={{ m: 2 }}>{iError.message}</Notice>
                : isos.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>{q ? `No ISO matches “${search.trim()}”.` : 'No ISOs yet. Create one: they get a login and add their own agents.'}</Typography>
                  : (
                    <Table size="small" sx={{ minWidth: 1100, ...tableSx }}>
                      <TableHead>
                        <TableRow>
                          {['ISO', 'Rates', 'We pay', 'Network', 'Restaurants', 'This month', 'Owed', 'Paid', ''].map(h => (
                            <TableCell key={h} sx={headCell}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isos.map(i => (
                          <TableRow key={i._id} sx={{ opacity: i.isActive ? 1 : 0.55 }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PortalName name={i.corporateName} disabled={openingPortal || !i.isActive} onClick={() => openPortal(i.vendorId)} />
                                {i.isActive && <AssignIcon name={i.corporateName} onClick={() => setPickingFor({ vendorId: i.vendorId, name: i.corporateName })} />}
                              </Box>
                              <Typography variant="caption" sx={{ color: T.muted, fontSize: 13, display: 'block' }}>{i.email}</Typography>
                              <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, color: T.text2 }}>Link code {i.code}</Typography>
                              {(!i.isActive || !i.isEmailVerified || !(i.bank && i.bank.isSet) || !i.taxId) && (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                                  {!i.isActive && <Badge kind="red" sx={{ fontSize: 11.5, px: 1 }}>Deactivated</Badge>}
                                  {!i.isEmailVerified && <Badge kind="amber" sx={{ fontSize: 11.5, px: 1 }}>Email not verified</Badge>}
                                  {!(i.bank && i.bank.isSet) && <Badge kind="amber" sx={{ fontSize: 11.5, px: 1 }}>No bank details</Badge>}
                                  {!i.taxId && <Badge kind="amber" sx={{ fontSize: 11.5, px: 1 }}>No tax ID</Badge>}
                                </Box>
                              )}
                              <Typography sx={{ color: i.lastLoginAt ? T.text2 : T.hint, fontSize: 12.5, mt: 0.25 }} title={i.lastLoginAt ? new Date(i.lastLoginAt).toLocaleString() : undefined}>
                                {i.lastLoginAt ? `Last login ${timeAgo(i.lastLoginAt)}` : 'No sign-in recorded yet'}
                              </Typography>
                            </TableCell>
                            <TableCell><RatesCell rates={i.rates} /></TableCell>
                            <TableCell sx={{ fontSize: 13.5, color: T.text2, whiteSpace: 'nowrap' }}>{i.payoutTo === 'AGENTS' ? 'ISO + agents' : 'ISO only'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {i.stats.activeAgents} agent{i.stats.activeAgents === 1 ? '' : 's'}
                              <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{i.stats.approved} approved of {i.stats.started}</Typography>
                            </TableCell>
                            <TableCell>
                              <RestaurantCount
                                total={i.stats.accounts} signedUp={i.stats.accountsSignedUp} assigned={i.stats.accountsAssigned}
                                onClick={() => setCreditedFor({ name: i.corporateName, isoId: i._id, vendorId: i.vendorId })}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {money(i.thisMonth && i.thisMonth.gross)}
                              <Typography sx={{ color: T.hint, fontSize: 12.5 }}>
                                {i.thisMonth ? `${i.thisMonth.orders} order${i.thisMonth.orders === 1 ? '' : 's'}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: T.amberText, fontWeight: 600 }}>{money(i.stats.pendingAmount)}</TableCell>
                            <TableCell>{money(i.stats.paidAmount)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <LinkButton size="small" onClick={() => setIsoDetails(i)}>Details</LinkButton>
                                <LinkButton size="small" onClick={() => setIsoDialog({ iso: i })}>Edit</LinkButton>
                                {i.isActive && <LinkButton size="small" disabled={openingPortal} onClick={() => openPortal(i.vendorId)} sx={{ whiteSpace: 'nowrap' }}>Open portal</LinkButton>}
                                <LinkButton size="small" onClick={() => setSignInsTarget({ _id: i.vendorId, name: i.corporateName, email: i.email })} sx={{ whiteSpace: 'nowrap' }}>Sign-ins</LinkButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )
          ) : tab === 3 ? (
            <Box>
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {FILTERS.map(([v, l]) => (
                  <Chip
                    key={l} label={l} onClick={() => setIsoPayoutFilter(v)} color={isoPayoutFilter === v ? 'primary' : 'default'} variant={isoPayoutFilter === v ? 'filled' : 'outlined'}
                    sx={chipSx}
                  />
                ))}
                <Typography sx={{ color: T.hint, fontSize: 13, ml: { sm: 1 } }}>Rows the ISO pays are shown for reference; the ISO marks those paid itself.</Typography>
              </Box>
              {pLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box> : (
                <PayoutsTable
                  payouts={isoPayouts}
                  showIso
                  emptyText="No ISO payouts here."
                  actions={p => (p.payer !== 'RUSHMYFOOD' ? null : p.status === 'PENDING' ? (
                    <>
                      <LinkButton size="small" disabled={settingIsoPayout} onClick={() => changeIsoPayout(p._id, 'PAID')}>Mark paid</LinkButton>
                      <LinkButton size="small" disabled={settingIsoPayout} onClick={() => changeIsoPayout(p._id, 'VOID')} sx={mutedLink}>Void</LinkButton>
                    </>
                  ) : (
                    <LinkButton size="small" disabled={settingIsoPayout} onClick={() => changeIsoPayout(p._id, 'PENDING')} sx={mutedLink}>Undo</LinkButton>
                  ))}
                />
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[['PENDING', 'Owed'], ['PAID', 'Paid'], ['VOID', 'Void'], ['', 'All']].map(([v, l]) => (
                  <Chip
                    key={l} label={l} onClick={() => setCommissionFilter(v)} color={commissionFilter === v ? 'primary' : 'default'} variant={commissionFilter === v ? 'filled' : 'outlined'}
                    sx={{ height: 34, px: 0.5, fontSize: 14, '&.MuiChip-outlined': { borderColor: T.input, color: T.text2, background: T.card }, '&.MuiChip-outlined:hover': { background: T.fill } }}
                  />
                ))}
              </Box>
              {cLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
                : commissions.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>No commissions here.</Typography>
                  : (
                    <Table size="small" sx={{ minWidth: 760, ...tableSx }}>
                      <TableHead>
                        <TableRow>
                          {['Earned', 'Agent', 'For', 'Amount', 'Status', ''].map(h => (
                            <TableCell key={h} sx={headCell}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commissions.map(c => {
                          const kind = COMMISSION_KIND[c.status] || COMMISSION_KIND.VOID
                          return (
                            <TableRow key={c._id}>
                              <TableCell sx={{ whiteSpace: 'nowrap', color: T.text2 }}>{day(c.createdAt)}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{c.agentName}</TableCell>
                              <TableCell>
                                {c.kind === 'ORDERS' ? (
                                  <>
                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Orders, {c.periodLabel}</Typography>
                                    <Typography sx={{ color: T.hint, fontSize: 13 }}>
                                      {c.ordersCount} order{c.ordersCount === 1 ? '' : 's'} · {money(c.subtotal)} subtotal
                                    </Typography>
                                    {(c.detail || []).filter(d => d.orders > 0).map(d => (
                                      <Typography key={d.restaurantId} sx={{ color: T.hint, fontSize: 12.5 }}>
                                        {d.restaurantName || `Restaurant #${d.restaurantId}`}: {money(d.amount)} ({pct(d.rate)} of {money(d.subtotal)}{d.extra > 0 ? `, plus ${money(d.extra)} above the minimums` : ''})
                                      </Typography>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{c.restaurantName}</Typography>
                                    <Typography sx={{ color: T.hint, fontSize: 13 }}>{c.note || 'Approved restaurant'}</Typography>
                                  </>
                                )}
                              </TableCell>
                              <TableCell>{money(c.amount)}</TableCell>
                              <TableCell>
                                <Badge kind={kind}>{c.status === 'PAID' ? `Paid ${day(c.paidAt)}` : c.status === 'PENDING' ? 'Owed' : 'Void'}</Badge>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {c.status === 'PENDING' && (
                                  <>
                                    <LinkButton size="small" disabled={settingStatus} onClick={() => changeStatus(c._id, 'PAID')}>Mark paid</LinkButton>
                                    <LinkButton size="small" disabled={settingStatus} onClick={() => changeStatus(c._id, 'VOID')} sx={mutedLink}>Void</LinkButton>
                                  </>
                                )}
                                {c.status !== 'PENDING' && (
                                  <LinkButton size="small" disabled={settingStatus} onClick={() => changeStatus(c._id, 'PENDING')} sx={mutedLink}>Undo</LinkButton>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  )}
            </Box>
          )}
        </Box>
      </Card>

      {dialog && (
        <AgentDialog
          agent={dialog.agent}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); refetch() }}
        />
      )}
      {isoDialog && (
        <IsoDialog
          iso={isoDialog.iso}
          convertFrom={isoDialog.convertFrom}
          onClose={() => setIsoDialog(null)}
          onSaved={() => {
            // A converted agent leaves the Agents tab for the ISOs tab.
            if (isoDialog.convertFrom) {
              setNotice(`${isoDialog.convertFrom.name} is now an ISO. Find them on the ISOs tab.`)
              refetchAll()
            } else {
              refetchIsos()
            }
            setIsoDialog(null)
          }}
        />
      )}
      {isoDetails && <IsoDetailsDialog iso={isoDetails} onClose={() => setIsoDetails(null)} />}
      {assigning && (
        <AssignRestaurantDialog
          presetVendorId={assigning.vendorId || null}
          onClose={() => setAssigning(null)}
          onDone={(message) => {
            setAssigning(null)
            setNotice(message)
            refetchAll()
          }}
        />
      )}
      {pickingFor && (
        <AssignRestaurantsDialog
          vendorId={pickingFor.vendorId}
          name={pickingFor.name}
          onClose={() => setPickingFor(null)}
          onChanged={refetchAll}
          onDone={(message) => {
            setPickingFor(null)
            setNotice(message)
            refetchAll()
          }}
        />
      )}
      {creditedFor && (
        <CreditedRestaurantsDialog
          name={creditedFor.name}
          vendorId={creditedFor.isoId ? null : creditedFor.vendorId}
          isoId={creditedFor.isoId || null}
          onClose={() => setCreditedFor(null)}
          onChanged={refetchAll}
          onAssign={() => { setPickingFor({ vendorId: creditedFor.vendorId, name: creditedFor.name }); setCreditedFor(null) }}
        />
      )}
      <SignInsDialog open={!!signInsTarget} onClose={() => setSignInsTarget(null)} account={signInsTarget} />
    </OnboardingPage>
  )
}
